import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, Code2, Palette, Share2, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SERVICES, PROCESS_STEPS, SERVICE_EXTRAS } from "@/data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = { Code2, Palette, Share2, TrendingUp };

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.slug === slug);

    useEffect(() => {
        if (!service) return;
        const id = "faq-jsonld";
        document.getElementById(id)?.remove();
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        script.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": service.faqs.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
        });
        document.head.appendChild(script);
        return () => document.getElementById(id)?.remove();
    }, [service]);

    if (!service) return <Navigate to="/services" replace />;
    const Icon = ICONS[service.icon];
    const extras = SERVICE_EXTRAS[service.slug];
    const others = SERVICES.filter((s) => s.slug !== slug);

    return (
        <main className="pt-32 sm:pt-40 pb-24">
            <Seo title={service.name} path={`/services/${service.slug}`} description={`${service.name} services in Long Beach, CA — ${service.short}`} />

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 items-center" data-testid="service-hero">
                <Reveal>
                    <Eyebrow>Services / {service.name}</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
                        {service.name}
                    </h1>
                    <p className="mt-5 text-lg text-slate-300 leading-relaxed">{service.short}</p>
                    <Link to={`/contact?service=${encodeURIComponent(service.name)}`} data-testid="service-cta-button"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-violet-600 px-7 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_28px_rgba(124,58,237,0.5)]">
                        {service.cta} <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="relative">
                        <img src={service.image} alt={`${service.name} at Innovative Fusion Hives`} className="rounded-2xl border border-white/10 aspect-[4/3] w-full object-cover" loading="eager" />
                        <div className="absolute -bottom-5 -left-4 rounded-xl border border-white/10 bg-[#101625]/95 backdrop-blur px-5 py-3 flex items-center gap-3">
                            <Icon className="h-5 w-5 text-amber-400" />
                            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300">Innovative Fusion Hives</span>
                        </div>
                    </div>
                </Reveal>
            </section>

            {extras && (
                <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-24" data-testid="service-approach">
                    <div className="grid lg:grid-cols-2 gap-12">
                        <Reveal>
                            <Eyebrow>Our Approach</Eyebrow>
                            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                How we think about {service.name.toLowerCase()}.
                            </h2>
                            {extras.approach.map((p) => (
                                <p key={p.slice(0, 24)} className="mt-5 text-base text-slate-400 leading-relaxed">{p}</p>
                            ))}
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="h-full rounded-2xl border border-white/10 bg-hive-card p-7 sm:p-8">
                                <h2 className="font-display text-lg font-bold text-white">Who this service is for</h2>
                                <ul className="mt-5 space-y-4">
                                    {extras.idealFor.map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-violet-400" /> {item}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 border-t border-white/10 pt-5 text-xs text-slate-500 leading-relaxed">
                                    Not sure if it fits? Send a project inquiry and we will tell you honestly whether {service.name.toLowerCase()} is the right investment for where your business is today.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </section>
            )}

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-24 grid gap-6 md:grid-cols-3" data-testid="service-details">
                <Reveal>
                    <div className="h-full rounded-2xl border border-white/10 bg-hive-card p-7">
                        <h2 className="font-display text-lg font-bold text-white">What We Offer</h2>
                        <ul className="mt-4 space-y-2.5">
                            {service.overview.map((o) => (
                                <li key={o} className="flex items-start gap-2 text-sm text-slate-300">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-violet-400" /> {o}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
                <Reveal delay={0.08}>
                    <div className="h-full rounded-2xl border border-white/10 bg-hive-card p-7">
                        <h2 className="font-display text-lg font-bold text-white">Client Benefits</h2>
                        <ul className="mt-4 space-y-2.5">
                            {service.benefits.map((b) => (
                                <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
                <Reveal delay={0.16}>
                    <div className="h-full rounded-2xl border border-white/10 bg-hive-card p-7">
                        <h2 className="font-display text-lg font-bold text-white">Typical Deliverables</h2>
                        <ul className="mt-4 space-y-2.5">
                            {service.deliverables.map((d) => (
                                <li key={d} className="flex items-start gap-2 text-sm text-slate-300">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> {d}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-24" data-testid="service-process">
                <Reveal className="max-w-2xl">
                    <Eyebrow>Process</Eyebrow>
                    <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">How a {service.name.toLowerCase()} project runs.</h2>
                    <p className="mt-4 text-sm text-slate-400 leading-relaxed">{service.processNote}</p>
                </Reveal>
                <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-6">
                    {PROCESS_STEPS.map((s) => (
                        <div key={s.n} className="bg-[#0E1320] p-5">
                            <p className="font-mono2 text-xs text-amber-400">{s.n}</p>
                            <h3 className="mt-2 font-display text-sm font-bold text-white">{s.name}</h3>
                        </div>
                    ))}
                </div>
                <Link to="/process" data-testid="service-process-link" className="mt-6 inline-block text-sm font-semibold text-violet-300 hover:text-violet-200">
                    See the full client journey →
                </Link>
            </section>

            <section className="mx-auto max-w-3xl px-4 sm:px-8 mt-24" data-testid="service-faqs">
                <Reveal>
                    <Eyebrow>FAQs</Eyebrow>
                    <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Common questions.</h2>
                </Reveal>
                <Accordion type="single" collapsible className="mt-8">
                    {service.faqs.map((f, i) => (
                        <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                            <AccordionTrigger data-testid={`service-faq-trigger-${i}`} className="text-left font-display font-semibold text-white hover:text-violet-300">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-sm text-slate-400 leading-relaxed">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-24" data-testid="service-final-cta">
                <Reveal>
                    <div className="rounded-2xl border border-violet-500/25 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(124,58,237,0.18),transparent_75%)] bg-hive-card p-10 sm:p-14 text-center">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Ready to talk {service.name.toLowerCase()}?</h2>
                        <p className="mx-auto mt-4 max-w-xl text-sm text-slate-400 leading-relaxed">
                            Tell us about your project and receive a reference-numbered inquiry response during business hours.
                        </p>
                        <Link to={`/contact?service=${encodeURIComponent(service.name)}`} data-testid="service-bottom-cta-button"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-violet-600 px-8 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_32px_rgba(124,58,237,0.5)]">
                            {service.cta} <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <div className="mt-10 flex flex-wrap justify-center gap-3">
                            {others.map((o) => (
                                <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`service-related-${o.slug}`}
                                    className="rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-400 hover:border-cyan-400/50 hover:text-white transition-colors">
                                    {o.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
